import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Inbox, Lock, ArrowUpDown, Star } from "lucide-react";
import { api, STATUS_META, normalizeStatus } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const FINAL = ["recuperee", "annulee"];
const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "priority", label: "⭐ Prioritaires" },
  { key: "unassigned", label: "Non attribuées" },
  { key: "mine", label: "Les miennes" },
  { key: "en_attente", label: "Reçues" },
  { key: "devis_envoye", label: "Devis proposé" },
  { key: "devis_valide", label: "Devis validé" },
  { key: "en_preparation", label: "En préparation" },
  { key: "prete", label: "Prêtes" },
  { key: "recuperee", label: "Récupérées" },
];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.en_attente;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.color}`}>{m.label}</span>;
}

export default function AdminRequests() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => setQuotes([]));
  }, []);

  const filtered = useMemo(() => {
    let list = quotes || [];
    if (filter === "unassigned") list = list.filter((q) => !q.assigned_to && !FINAL.includes(normalizeStatus(q.status)));
    else if (filter === "mine") list = list.filter((q) => q.assigned_to === user?.id);
    else if (filter === "priority") list = list.filter((q) => q.priority && !FINAL.includes(normalizeStatus(q.status)));
    else if (filter !== "all") list = list.filter((q) => normalizeStatus(q.status) === filter);

    const s = search.trim().toLowerCase();
    if (s) {
      list = list.filter((q) =>
        [q.ref, q.user_name, q.user_email, q.vehicle?.brand, q.vehicle?.model, q.vehicle?.vin, q.parts_description, q.assigned_to_name]
          .filter(Boolean).some((f) => String(f).toLowerCase().includes(s)));
    }
    const arr = [...list];
    if (sort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "oldest") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "amount") arr.sort((a, b) => (b.quote?.total || 0) - (a.quote?.total || 0));
    // Float active priority requests to the top (stable)
    const w = (q) => (q.priority && !FINAL.includes(normalizeStatus(q.status)) ? 1 : 0);
    arr.sort((a, b) => w(b) - w(a));
    return arr;
  }, [quotes, filter, search, sort, user]);

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight">Demandes</h1>
      <p className="mt-1.5 text-sm text-slate-500">Recherchez, prenez en charge et traitez les demandes clients.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="requests-search"
            placeholder="Rechercher n° STK, client, véhicule, VIN, pièce..." className="pl-9" />
        </div>
        <div className="relative">
          <ArrowUpDown className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="requests-sort"
            className="h-10 rounded-md border border-slate-200 bg-white pl-9 pr-8 text-sm font-medium text-slate-600">
            <option value="recent">Plus récentes</option>
            <option value="oldest">Plus anciennes</option>
            <option value="amount">Montant décroissant</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === f.key ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {quotes === null ? (
          <div className="py-16 text-center text-slate-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center"><Inbox className="mx-auto h-10 w-10 text-slate-300" /><p className="mt-3 text-sm text-slate-500">Aucune demande trouvée.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-quotes-table">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-semibold">N°</th>
                  <th className="px-5 py-3 font-semibold">Client</th>
                  <th className="px-5 py-3 font-semibold">Véhicule</th>
                  <th className="hidden px-5 py-3 font-semibold md:table-cell">Prise en charge</th>
                  <th className="px-5 py-3 font-semibold">Statut</th>
                  <th className="px-5 py-3 text-right font-semibold">Montant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((q) => {
                  const mine = q.assigned_to === user?.id;
                  const isPriority = q.priority && !FINAL.includes(normalizeStatus(q.status));
                  return (
                    <tr key={q.id} className={`hover:bg-slate-50 ${isPriority ? "bg-amber-50/40" : ""}`}>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs font-semibold text-slate-500" data-testid={`row-ref-${q.id}`}>{q.ref || "—"}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {isPriority && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" data-testid={`priority-star-${q.id}`} />}
                          <Link to={`/app/demande/${q.id}`} className="font-medium text-slate-800 hover:text-blue-600" data-testid={`admin-quote-${q.id}`}>{q.user_name}</Link>
                        </div>
                        <p className="text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString("fr-FR")}</p>
                      </td>
                      <td className="px-5 py-4 text-slate-600">{q.vehicle?.brand} {q.vehicle?.model}</td>
                      <td className="hidden px-5 py-4 md:table-cell">
                        {q.assigned_to_name ? (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${mine ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                            <Lock className="h-3 w-3" /> {q.assigned_to_name}{mine ? " (vous)" : ""}
                          </span>
                        ) : <span className="text-xs text-slate-400">Libre</span>}
                      </td>
                      <td className="px-5 py-4"><StatusBadge status={q.status} /></td>
                      <td className="px-5 py-4 text-right font-semibold text-blue-600">{q.quote ? `${Number(q.quote.total ?? q.quote.price).toFixed(2)} €` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
